<div class="dialog--title" cdkDrag cdkDragHandle cdkDragRootElement=".cdk-overlay-pane" [cdkDragFreeDragPosition]="position" (cdkDragEnded)="onDragEnded($event)">
  <h6 mat-dialog-title>
    {{ titleHeader }}, {{ data.event.start | date }}
    @if (isDaySummary && (exhausted || overdrawn)) {
      -
      <span
        [ngClass]="{ 'exhausted-highlight': exhausted, 'overdrawn-highlight': overdrawn }">
        {{ exhausted ? data.sysText.exhausted : data.sysText.overdrawn }}
      </span>
    }
  </h6>
  @if (isDaySummary) {
    <!-- buttons so keyboard users can reach the info too: Material shows the tooltip on focus, and screen readers read it as the button's description -->
    <button
      type="button"
      class="button-reset info-button"
      [attr.aria-label]="data.sysText.moreInformation_ariaLabel"
      [matTooltip]="data.sysText.hoursItem_tooltip"
      [matTooltipShowDelay]="500"
      matTooltipPosition="after">
      <mat-icon class="button-icon">info_outline</mat-icon>
    </button>
  }
  @if (isMobile$ | async) {
    <span class="spacer"></span>
    <button type="button" class="button-reset" (click)="close()" [attr.aria-label]="data.sysText.close" [disabled]="isSubmitting$ | async">
      <mat-icon class="button-icon">close</mat-icon>
    </button>
  }
</div>

<mat-dialog-content>

  <!-- View Action Item -->

  @if (eventProps.eventType == calendarEventTypes.ActionItem) {
    <div class="main-content flex main-content-event">
      <p class="body2">
        {{ data.event.title }}
      </p>
    </div>
  }

  <!-- View Hours Worked -->

  @if (eventProps.eventType == calendarEventTypes.HoursWorked) {
    <div class="main-content flex main-content-event">
      <p class="body2">
        {{ data.event.title }}
      </p>
    </div>
  }

  <!-- View Leave Hour Request -->

  @if (eventProps.eventType == calendarEventTypes.RequestForLeaveHours) {
    @let request = employeeLeaveHoursRequests?.[0];
    <div class="section-header-container extra-margin">
      <h2 class="overline grey1 semibold section-title">
        {{ data.sysText.leaveHoursRequest }}
      </h2>
      <mat-divider class="section-header-underline"></mat-divider>
    </div>
    <div class="main-content flex leave-hour-request">
      @if (request) {
        <ng-container
          [ngTemplateOutlet]="item"
          [ngTemplateOutletContext]="{
            label: data.sysText.submittedOn,
            value: (request.submittedOn | date: 'MMM d, y h:mm a')
          }">
        </ng-container>
      }
      @if (request?.submittedOn) {
        <ng-container
          [ngTemplateOutlet]="item"
          [ngTemplateOutletContext]="{
            label: data.sysText.pendingHours,
            value: request.hours + 'h'
          }">
        </ng-container>
      }
    </div>
  }

  <!-- View Calendar Day Summary -->

  @if (isDaySummary) {
    <!-- the taken / drawing counts' explanations say whether they include this case, or leave it out while it's hidden -->
    @let caseHoursTip = (hasCaseHoursToday() ? ' ' + data.sysText.includingThisCase : '')
      + (caseIsHidden() ? ' ' + data.sysText.excludingHiddenCase : '');
    <div class="section-header-container">
      <h2 class="overline grey1 semibold section-title">
        {{ data.sysText.thisDate }}
      </h2>
      <mat-divider class="section-header-underline"></mat-divider>
    </div>
    <div class="main-content flex">
      <div class="column-content">
        @for (bank of bankSummaries; track $index) {
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: (data.sysText.bankPeriod | replaceAll: '@[bank]': bank.name),
              tip: (data.sysText.caseBankPeriod_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.casePeriod ?? '--'
            }">
          </ng-container>
        }
        @if (leaveInformation?.isIntermittent == true) {
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.pendingRequests,
              tip: data.sysText.thisDatePendingRequests_tooltip,
              value: getThisDatePendingRequests() ?? 0
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.pendingHours,
              tip: data.sysText.thisDatePendingHours_tooltip,
              value: getThisDatePendingHours() ?? '--'
            }">
          </ng-container>
        }
      </div>
      <div class="column-content">
        <ng-container
          [ngTemplateOutlet]="item"
          [ngTemplateOutletContext]="{
            label: data.sysText.leavesTaken,
            tip: data.sysText.leavesTaken_tooltip + caseHoursTip,
            value: employeeLeaveHoursExtended?.leavesCount ?? 0
          }">
        </ng-container>
        <ng-container
          [ngTemplateOutlet]="item"
          [ngTemplateOutletContext]="{
            label: data.sysText.hoursTaken,
            tip: data.sysText.hoursTaken_tooltip + caseHoursTip,
            value: employeeLeaveHoursExtended?.employeeLeaveHours ?? '--'
          }">
        </ng-container>
      </div>
    </div>
    @if (data.caseDetails != null) {
      <div class="section-header-container">
        <h2 class="overline grey1 semibold section-title">
          {{ data.sysText.thisCase }}
        </h2>
        <mat-divider class="section-header-underline"></mat-divider>
        @if (outsideCaseTimeframe()) {
          <div class="body2 grey2 warn">
            {{ data.sysText.outsideCaseTimeframeMessage }}
          </div>
        }
        @if (notWorkday()) {
          <div class="body2 grey2 warn">
            {{ data.sysText.notWorkdayMessage }}
          </div>
        }
        @if (exceedsWorkdayHours()) {
          <div class="body2 grey2 warn">
            {{ data.sysText.exceedsWorkdayHoursMessage }}
          </div>
        }
        @if (caseIsHidden()) {
          <div class="body2 grey2">
            {{ data.sysText.hiddenCaseMessage }}
          </div>
        }
      </div>
      <div class="main-content flex">
        <div class="column-content">
          @if (leaveInformation?.isIntermittent == true) {
            <ng-container
              [ngTemplateOutlet]="item"
              [ngTemplateOutletContext]="{
                label: data.sysText.pendingRequests,
                tip: data.sysText.thisCasePendingRequests_tooltip,
                value: employeeLeaveHoursRequests ? employeeLeaveHoursRequests.length : 0
              }">
            </ng-container>
            <ng-container
              [ngTemplateOutlet]="item"
              [ngTemplateOutletContext]="{
                label: data.sysText.pendingHours,
                tip: data.sysText.thisCasePendingHours_tooltip,
                value: getThisCasePendingHours() ?? '--'
              }">
            </ng-container>
          }
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: (data.sysText.hours | titlecase),
              tip: data.sysText.casehours_tooltip,
              value: employeeLeaveHoursExtended?.leaveCaseLeaveHours ?? '--'
            }">
          </ng-container>
          @for (bank of bankSummaries; track $index) {
            <ng-container
              [ngTemplateOutlet]="item"
              [ngTemplateOutletContext]="{
                label: (data.sysText.thisCaseBankEffectiveBanks | replaceAll: '@[bank]': bank.name),
                tip: (data.sysText.thisCaseBankEffectiveBanks_tooltip | replaceAll: '@[bank]': bank.name),
                value: bank.caseEffectiveHours ?? '--',
                note: bank.fullDayWarn ? data.sysText.notFullWorkdayParen : null,
                highlight: bank.fullDayWarn ? 'overdrawn' : null
              }">
            </ng-container>
          }
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.used,
              tip: data.sysText.used_tooltip + (hasCaseHoursToday() ? ' ' + data.sysText.includingThisDate : ''),
              value: employeeLeaveHoursExtended?.leaveCaseUsedHours ?? '--'
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.future,
              tip: data.sysText.future_tooltip,
              value: employeeLeaveHoursExtended?.leaveCaseFutureHours ?? '--'
            }">
          </ng-container>
        </div>
        <div class="column-content">
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.leaveType,
              tip: data.sysText.leaveType_tooltip,
              value: (leaveInformation?.leaveType?.description ?? '--')
                + (leaveInformation?.leaveSubtype != null ? ' (' + leaveInformation.leaveSubtype + ')' : '')
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.draw,
              tip: data.sysText.draw_tooltip,
              value: drawBanks ?? '--'
            }">
          </ng-container>
        </div>
      </div>
    }
    @for (bank of bankSummaries; track $index) {
      <div class="section-header-container">
        <h2 class="overline grey1 semibold section-title">{{ data.sysText.bankSummary | replaceAll: '@[bank]': bank.name }}</h2>
        <mat-divider class="section-header-underline"></mat-divider>
      </div>
      <div class="main-content flex">
        <div class="column-content">
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.leaveYear,
              tip: (data.sysText.bankLeaveYear_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.leaveYear ?? '--'
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.period,
              tip: (data.sysText.bankPeriod_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.period ?? '--'
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.caseUsed,
              tip: (data.sysText.bankCaseUsed_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.caseUsedHours ?? '--'
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.caseFuture,
              tip: (data.sysText.bankCaseFuture_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.caseFutureHours ?? '--'
            }">
          </ng-container>
        </div>
        <div class="column-content">
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.leavesTaken,
              tip: (data.sysText.bankLeavesTaken_tooltip | replaceAll: '@[bank]': bank.name) + caseHoursTip,
              value: bank.leavesCount ?? 0
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.leavesDrawing,
              tip: (data.sysText.bankLeavesDrawing_tooltip | replaceAll: '@[bank]': bank.name) + caseHoursTip,
              value: bank.drawCount ?? 0
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.employeeTotal,
              tip: (data.sysText.bankEmployeeTotal_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.employeeTotalHours ?? '--'
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.employeeUsed,
              tip: (data.sysText.bankEmployeeUsed_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.employeeUsedHours ?? '--'
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.employeeAdjustments,
              tip: (data.sysText.bankEmployeeAdjustments_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.employeeAdjustments ?? '--'
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.employeeAvailable,
              tip: (data.sysText.bankEmployeeAvailable_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.employeeAvailableHours ?? '--',
              note: bank.exhausted ? data.sysText.exhaustedParen : bank.overdrawn ? data.sysText.overdrawnParen : null,
              highlight: bank.exhausted ? 'exhausted' : bank.overdrawn ? 'overdrawn' : null
            }">
          </ng-container>
          <ng-container
            [ngTemplateOutlet]="item"
            [ngTemplateOutletContext]="{
              label: data.sysText.employeeFuture,
              tip: (data.sysText.bankEmployeeFuture_tooltip | replaceAll: '@[bank]': bank.name),
              value: bank.employeeFutureHours ?? '--'
            }">
          </ng-container>
        </div>
      </div>
    }
  }
</mat-dialog-content>

<!-- show the actions if there is at least one button to show -->
<!-- actions class only if two buttons are being shown -->
@if (canReviewRequest || !(isMobile$ | async)) {
  <mat-dialog-actions
    [class.space-between]="canReviewRequest && !(isMobile$ | async)"
    align="end">
    @let isSubmitting = isSubmitting$ | async;
    @if (canReviewRequest) {
      <!-- the button keeps focus while its dialog loads: showProgress lays the spinner over its label, disabledInteractive keeps it focusable, and the guard blocks clicks -->
      <button
        mat-stroked-button
        class="button"
        color="accent"
        [disabled]="isSubmitting"
        disabledInteractive
        [showProgress]="isSubmitting"
        [attr.aria-label]="isSubmitting ? data.sysText.loading_ariaLabel : null"
        (click)="!isSubmitting && onReviewRequest()">
        {{ data.sysText.reviewRequest }}
        <mat-spinner progressIndicator diameter="30" color="accent"></mat-spinner>
      </button>
    }
    @if (!(isMobile$ | async)) {
      <button
        mat-stroked-button
        class="button"
        mat-dialog-close
        color="accent"
        [disabled]="isSubmitting">
        {{ data.sysText.close }}
      </button>
    }
  </mat-dialog-actions>
}

<!-- one label and value row. A label with an explanation (tip) is a toggletip: the label is its name, the explanation
its tooltip (read as its description). highlight colors the row ('exhausted' or 'overdrawn'), and note follows the
value in normal weight -->
<ng-template
  #item
  let-label="label"
  let-tip="tip"
  let-value="value"
  let-note="note"
  let-highlight="highlight">
  <div
    class="item-container"
    [ngClass]="{ 'item-container-exhausted': highlight == 'exhausted', 'item-container-overdrawn': highlight == 'overdrawn' }">
    <div
      class="item-label"
      [class.no-hover]="!tip">
      <p class="body2 grey2">
        @if (tip) {
          <button
            type="button"
            class="button-reset toggletip"
            [matTooltip]="tip">
            {{ label }}
          </button>
        } @else {
          {{ label }}
        }
      </p>
    </div>
    <div class="item-content">
      <p class="body2 grey1 semibold">
        {{ value }}
        @if (note) {
          <span class="item-content-nobold">{{ note }}</span>
        }
      </p>
    </div>
  </div>
</ng-template>
